import { VideoJsPlaybackProvider } from '@app/core/playback/video/VideoPlaybackProvider';
import { Box } from '@mui/material';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { ResizableDialog } from '../../components/dialog/ResizableDialog';
import { useStationLiveHlsUrl } from '../../modules/logger/screens/stream/modes/video/useStationLiveHlsUrl';
import { GlobalLivePlayerHeader } from './components/GlobalLivePlayerHeader';
import { StationLivePlayer } from './components/StationLivePlayer';
import { useLivePlayerSettingsStore } from './store/live-player-settings.store';

/**
 * Conditionally renders LivePlayer based on:
 * - Always shows when on logger stream screen (/logger/s/:slug)
 * - Shows when pinLivePlayer setting is true, even when not on logger screen
 * - Does NOT show when on the dedicated live player screen (/live-player/:station) to avoid duplicate player
 */
export function GlobalLivePlayer() {
  const { $t } = useIntl();
  const location = useLocation();
  const livePlayerStationId = useLivePlayerSettingsStore((state) => state.stationId);
  const isPiPActive = useLivePlayerSettingsStore((state) => state.isPiPActive);
  const setStationClock = useLivePlayerSettingsStore((state) => state.setStationClock);
  const src = useStationLiveHlsUrl(livePlayerStationId ?? '');

  // Hide floating dialog when user is on the dedicated live player screen
  if (/^\/live-player\/[^/]+$/.test(location.pathname)) {
    return null;
  }

  if (!livePlayerStationId) {
    return null;
  }

  if (isPiPActive) {
    // Do NOT return null here: PiP window needs the source video DOM to stay mounted.
    // Unmounting the dialog removed the player and caused the PiP window to show white.
  }

  if (!src) {
    return null;
  }

  return (
    <ResizableDialog
      open={true}
      title={$t({
        id: 'globalLivePlayer.title',
        defaultMessage: 'Reproductor en vivo',
      })}
      initialSize={{ width: 400, height: 280 }}
      hidden={isPiPActive}
    >
      <VideoJsPlaybackProvider
        playbackId="live-player"
        exclusivePlaybackMode="muted"
        src={src}
        key={src}
      >
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            '& .video-js, & video, &.vjs-control-bar': {
              borderBottomLeftRadius: '6px',
              borderBottomRightRadius: '6px',
            },
          }}
        >
          <GlobalLivePlayerHeader stationId={livePlayerStationId} />
          <Box sx={{ borderRadius: 2 }}>
            <StationLivePlayer stationId={livePlayerStationId} onClockUpdate={setStationClock} />
          </Box>
        </Box>
      </VideoJsPlaybackProvider>
    </ResizableDialog>
  );
}
